import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { toast } from 'sonner';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login({ email, password });
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      const errorMessage = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F9F6F0' }}>
      <div 
        className="w-full max-w-md p-8 rounded-md border border-[#E5E0D8]" 
        style={{ backgroundColor: '#FFFFFF' }}
        data-testid="login-form"
      >
        <h1 
          className="text-4xl mb-2 tracking-tight" 
          style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
        >
          Welcome Back
        </h1>
        <p className="mb-6" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
          Login to continue to eatfromlocal
        </p>

        {error && (
          <div 
            className="mb-4 p-3 rounded-md" 
            style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
            data-testid="error-message"
          >
            <p style={{ fontFamily: 'Manrope', fontSize: '14px' }}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label 
              htmlFor="email" 
              className="block mb-1" 
              style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="email-input"
            />
          </div>

          <div>
            <label 
              htmlFor="password" 
              className="block mb-1" 
              style={{ fontFamily: 'Manrope', fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-md py-2"
            style={{ 
              backgroundColor: '#D95A41', 
              fontFamily: 'Manrope', 
              fontWeight: 600 
            }}
            data-testid="login-submit-btn"
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px" style={{ backgroundColor: '#E5E0D8' }}></div>
          <span
            className="uppercase"
            style={{ fontFamily: 'Manrope', fontSize: '11px', color: '#5C5C5C', letterSpacing: '0.2em' }}
          >
            or
          </span>
          <div className="flex-1 h-px" style={{ backgroundColor: '#E5E0D8' }}></div>
        </div>

        <GoogleSignInButton label="Sign in with Google" />

        <p className="mt-6 text-center" style={{ fontFamily: 'Manrope', fontSize: '14px', color: '#5C5C5C' }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: '#D95A41', fontWeight: 600 }} data-testid="register-link">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};